import type { ComponentPropsWithoutRef, ComponentType } from 'react';

import { ToolIconsV4 } from '@/shared/ui/GeneralIcons/GeneralIcons.v4';

import {
  TOOL_BADGE_GLYPH_SIZES,
  toToolBadgeCategory,
  toolBadgeVariants,
  type ToolBadgeSize,
  type ToolBadgeVariantProps,
} from './ToolBadge.variants';

/** Lucide icons and the app's own `GeneralIcons` both fit. */
export type ToolBadgeIcon = ComponentType<{ size?: number; className?: string }>;

export interface ToolBadgeProps
  extends Omit<ComponentPropsWithoutRef<'span'>, 'children'>, Pick<ToolBadgeVariantProps, 'shape'> {
  toolId: string;
  /** Any casing; an empty or unknown tag renders as `deliver`. */
  category?: string;
  /** Replaces the icon looked up from `toolId`. */
  icon?: ToolBadgeIcon;
  size?: ToolBadgeSize;
  /** For a host that animates the glyph apart from its tile. */
  glyphClassName?: string;
}

/**
 * A tool's icon on a tile in its category colour. Decorative by default,
 * because a tool's name almost always sits beside it; a host that is the only
 * label passes `role` and `aria-label` and drops `aria-hidden`.
 */
export function ToolBadge({
  toolId,
  category,
  icon: Icon,
  size = 'lg',
  shape = 'tile',
  className,
  glyphClassName,
  ...rest
}: ToolBadgeProps) {
  const resolved = toToolBadgeCategory(category);
  const glyphSize = TOOL_BADGE_GLYPH_SIZES[size];

  return (
    <span
      aria-hidden
      data-slot="tool-badge"
      data-category={resolved}
      className={toolBadgeVariants({ category: resolved, shape, size, className })}
      {...rest}
    >
      {Icon ? (
        <Icon size={glyphSize} className={glyphClassName} />
      ) : (
        ToolIconsV4(toolId, resolved, glyphSize, glyphClassName, 'currentColor')
      )}
    </span>
  );
}
